import axios from 'axios';

const API_BASE = '/api';

const api = axios.create({
  baseURL: API_BASE,
});

// Attach the access token to every outgoing request
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// If a request returns 401, try to refresh the access token once.
// Auth endpoints are excluded — they intentionally return 401s as normal
// business logic (wrong password, locked account, bad MFA code, etc.) and
// must never trigger a redirect or a refresh attempt.
const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/mfa/verify',
  '/auth/mfa/verify-recovery',
  '/auth/mfa-enrollment/begin',
  '/auth/mfa-enrollment/confirm',
  '/auth/force-password-change',
  '/auth/password-reset/confirm',
  '/auth/password-reset/request',
];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't intercept 401s from auth endpoints — let the calling component
    // handle them and display the appropriate error message to the user.
    const isAuthEndpoint = AUTH_ENDPOINTS.some(path =>
      originalRequest.url?.includes(path)
    );
    if (isAuthEndpoint) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = sessionStorage.getItem('refresh_token');
      if (!refreshToken) {
        // No refresh token — force logout
        sessionStorage.clear();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_BASE}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const newAccessToken = response.data.access_token;
        sessionStorage.setItem('access_token', newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed — force logout
        sessionStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;